<template>
  <div class="windy-container">
    <iframe
      ref="windyIframe"
      :src="windySrc"
      @load="onIframeLoad"
      class="windy-iframe"
    ></iframe>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, computed, defineExpose } from 'vue';

const props = defineProps<{
  devices?: any[];
  zoom?: number;
  center?: any;
  layer?: string;
}>();

const windyIframe = ref<HTMLIFrameElement | null>(null);
const publicPath = computed(() => process.env.BASE_URL || '/');
// windy.html is rewritten at container start with the current WINDY_KEY. A
// cache-buster per mount keeps a stale copy (browser heuristic cache, proxy)
// from serving a revoked key; the file is ~6 KB, so always fetching it is cheap.
const windySrc = `${publicPath.value}windy.html?v=${Date.now()}`;

function onIframeLoad() {
  if (!windyIframe.value || !windyIframe.value.contentWindow) return;

  const iframeWin = windyIframe.value.contentWindow;

  iframeWin.postMessage({
    type: 'INIT_WINDY',
    payload: {
      center: props.center ? [props.center.lat, props.center.lng] : [45.4642, 9.1900],
      zoom: props.zoom || 10,
      layer: props.layer || 'wind'
    }
  }, '*');

  updateMarkers();
}

function updateMarkers() {
  if (!windyIframe.value || !windyIframe.value.contentWindow) return;

  const sanitizedDevices = (props.devices || []).map(d => ({
    lat: d.lat || d.latitude || d.latest_data?.latitude,
    lng: d.lng || d.longitude || d.latest_data?.longitude,
    name: d.name,
    vin: d.vin
  }));

  windyIframe.value.contentWindow.postMessage({
    type: 'UPDATE_MARKERS',
    payload: sanitizedDevices
  }, '*');
}

watch(() => props.devices, () => {
  updateMarkers();
}, { deep: true });

watch(() => props.layer, (newLayer) => {
  if (!windyIframe.value || !windyIframe.value.contentWindow) return;
  
  windyIframe.value.contentWindow.postMessage({
    type: 'SET_OVERLAY',
    payload: newLayer
  }, '*');
});

function flyTo(lat: number, lng: number, zoom?: number) {
  if (!windyIframe.value || !windyIframe.value.contentWindow) return;
  windyIframe.value.contentWindow.postMessage({
    type: 'SET_CENTER',
    payload: { lat, lng, zoom }
  }, '*');
}

defineExpose({ flyTo });

onBeforeUnmount(() => {
  // Cleanup if needed
});
</script>

<style scoped>
.windy-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  pointer-events: auto;
}

.windy-iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
